import React, { useMemo } from "react";
import { useModerationQueue } from "hooks/useModeration";
import Pagination from "views/pages/components/Pagination";
import { Avatar } from "@mui/material";
import constants from "constants";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import SimpleHeader from "components/Headers/SimpleHeader";
import {
  Card,
  Container,
  Input,
  Button,
  Badge,
  Spinner,
  Alert,
  Table
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import useNotificationHighlight from "hooks/useNotificationHighlight";

function AppealsQueue() {
  const { t } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();
  const navigate = useNavigate();

  const {
    data,
    totalItems,
    loading,
    error,
    page,
    limit,
    search,
    setSearch,
    handlePageChange,
    handleLimitChange,
    refresh
  } = useModerationQueue("appeals");

  const enrichedData = useMemo(() => {
    const now = Date.now();
    return (data || []).map((appeal) => {
      const submittedAt = appeal.submittedAt ? new Date(appeal.submittedAt).getTime() : 0;
      const diffDays = submittedAt ? Math.max(0, Math.floor((now - submittedAt) / (1000 * 60 * 60 * 24))) : 0;
      return { ...appeal, _daysWaiting: diffDays };
    });
  }, [data]);

  const getAppealStatusColor = (status) => {
    if (status === "accepted") return "success";
    if (status === "rejected") return "danger";
    if (status === "underReview" || status === "under_review" || status === "pending") return "warning";
    return "secondary";
  };

  return (
    <>
      <SimpleHeader name={t("sideNav.appealsQueue", "Appeals Queue")} parentName={t("sideNav.moderation", "Moderation")} />
      
      <Container className="mt--6" fluid>
        {error && (
          <Alert color="danger" className="shadow border-0 mb-4">
            {error}
            <div className="mt-3">
              <Button color="secondary" size="sm" onClick={refresh}>
                {t("common.retry", "Retry")}
              </Button>
            </div>
          </Alert>
        )}

        <Card className="shadow border-0">
          <div className="pt-4 pb-0">
            {/* Unified Search/Filter Header */}
            <div className="d-flex flex-column flex-md-row pb-4 justify-content-between align-items-start align-items-md-center px-4">
              <div className="dataTables_filter d-flex align-items-center mb-2 mb-md-0">
                <label className="d-flex align-items-center mb-0">
                  <span className="text-sm font-medium text-slate-600 mr-2">
                    {t("table.search", "Search")}:
                  </span>
                  <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                    <Input
                      className="form-control-sm"
                      placeholder={t("categories.moderation.searchUser", "Search by user...")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ paddingRight: "24px" }}
                    />
                    {search && (
                      <span
                        onClick={() => setSearch("")}
                        className="position-absolute cursor-pointer search-clear-btn"
                        style={{ right: "8px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}
                      >
                        <CloseIcon style={{ fontSize: "14px" }} />
                      </span>
                    )}
                  </div>
                </label>
              </div>
              <Button size="sm" color="neutral" onClick={refresh} className="mr-4">
                <i className="fa fa-sync mr-1" />
                {t("common.refresh", "Refresh Queue")}
              </Button>
            </div>
          </div>

          {/* Tabular Queue List */}
          {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>{t("table.headers.user", "User")}</th>
                      <th>{t("table.headers.appealText", "Appeal Text")}</th>
                      <th>{t("table.headers.daysWaiting", "Days Waiting")}</th>
                      <th>{t("table.headers.appealStatus", "Appeal Status")}</th>
                      <th>{t("table.headers.dateAppealed", "Date Appealed")}</th>
                      <th>{t("table.headers.action", "Action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          {t("categories.moderation.appeals.noAppeals", "No pending appeals found.")}
                        </td>
                      </tr>
                    ) : (
                      enrichedData.map((appeal) => {
                        const userObj = appeal.userId || {};
                        const userName = userObj?.firstName
                          ? `${userObj.firstName} ${userObj.lastName || ""}`
                          : t("categories.moderation.unknown", "Unknown User");
                        const userPic = userObj?.profilePictureUrl
                          ? `${constants.s3Bucket}/profile-pics/${userObj.profilePictureUrl}`
                          : null;

                        const highlighted = isHighlighted("appeal", appeal._id) || isHighlighted("support_ticket", appeal._id);

                        return (
                          <tr key={appeal._id} className={highlighted ? "border-left-highlight" : "hover:bg-neutral"}>
                            <td>
                              <div className="d-flex align-items-center">
                                {userPic ? (
                                  <Avatar src={userPic} sx={{ width: 32, height: 32 }} />
                                ) : (
                                  <Avatar
                                    {...helpers.stringAvatar(userName)}
                                    sx={{ width: 32, height: 32, fontSize: 14 }}
                                  />
                                )}
                                <div className="ml-2">
                                  <span className="font-weight-bold d-block">{userName}</span>
                                  <small className="text-muted">{userObj.email}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-wrap text-slate-700 font-weight-500" style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {appeal.appealText}
                            </td>
                            <td>
                              <Badge color={appeal._daysWaiting >= 7 ? "danger" : (appeal._daysWaiting >= 3 ? "warning" : "success")} className="badge-pill">
                                {appeal._daysWaiting} {appeal._daysWaiting === 1 ? t("categories.moderation.day", "day") : t("categories.moderation.days", "days")}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={getAppealStatusColor(appeal.status)} className="badge-pill text-uppercase" style={{ fontSize: "10px" }}>
                                {t("page.flagged.status." + appeal.status, appeal.status)}
                              </Badge>
                            </td>
                            <td>
                              {helpers.getFormattedDate(appeal.submittedAt, t)}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                onClick={() => navigate(`/admin/moderation/appeals/${appeal._id}/details`)}
                                title={t("categories.moderation.viewDetails", "View Details")}
                              >
                                <VisibilityIcon fontSize="small" color="primary" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading && enrichedData.length > 0 && (
              <div className="py-3 px-4 border-top">
                <div className="pagination-override-margin">
                  <style>{`
                    .pagination-override-margin .pagination-container {
                      margin-bottom: 0px !important;
                      margin-top: 0px !important;
                    }
                  `}</style>
                  <Pagination
                    totalRows={totalItems}
                    rowsPerPage={limit}
                    currentPage={page - 1}
                    onPageChange={(p) => handlePageChange(p + 1)}
                    onRowsPerPageChange={(l) => handleLimitChange(l)}
                    t={t}
                  />
                </div>
              </div>
            )}
        </Card>
      </Container>
    </>
  );
}

export default AppealsQueue;
